// shell/session.js — minimal session-key reader for the app shell.
// Purpose: the router guard (app.js in the deployed build) and the top bar need to
// know whether a session exists WITHOUT depending on the auth engine's export shape.
// The authoritative cookie WRITER (setSession: 30-day `session_key` cookie) lives in
// the auth engine (src/api). This shell only READS the same cookie/localStorage the
// guard checks — a faithful, dependency-free copy of the read side of `0ce8.js`.
// Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
// (cookie name `session_key`, localStorage keys — AUTH-SESSION §2.1/§2.4),
// internal impl re-authored.

// VERBATIM — Config.cookies.key._default = "session_key".
export const SESSION_COOKIE = 'session_key'

/** Read a cookie by name (read side of 0ce8.js export "f"). */
export function getCookie(name) {
  if (typeof document === 'undefined') return undefined
  const prefix = name + '='
  const parts = document.cookie ? document.cookie.split('; ') : []
  for (const part of parts) {
    if (part.indexOf(prefix) === 0) {
      return decodeURIComponent(part.slice(prefix.length))
    }
  }
  return undefined
}

/** The value the guard treats as "authenticated": cookie OR localStorage flag. */
export function readSessionKey() {
  const fromCookie = getCookie(SESSION_COOKIE)
  if (fromCookie) return fromCookie
  try {
    return window.localStorage.getItem(SESSION_COOKIE) || undefined
  } catch (e) {
    return undefined
  }
}

/** True when a session is present (drives the guard + the top bar). */
export function hasSession() {
  return Boolean(readSessionKey())
}

/**
 * Best-effort local logout used by the shell top bar when the auth engine's
 * `user/logOut` action is not registered. Clears the cookie + local flags; the
 * engine's own logOut (which also POSTs user/logout) is preferred when present.
 */
export function clearSessionLocal() {
  if (typeof document !== 'undefined') {
    document.cookie = SESSION_COOKIE + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
  }
  try {
    window.localStorage.removeItem(SESSION_COOKIE)
    window.localStorage.removeItem('master-session-key')
  } catch (e) {
    /* ignore */
  }
}
